<script setup>
import { ref, computed, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useCarsGroupsStore } from '@/stores/cars-groups'

import Modal from '@/components/UTAModal.vue'
import UTACreateCarsGroup from '@/components/groupsCars/UTACreateCarsGroup.vue'

const translaterStore = useTranslaterStore()
const carsGroupsStore = useCarsGroupsStore()

const getTranslate              = computed(() => translaterStore.getTranslate)
const carsGroups                = computed(() => carsGroupsStore.carsGroups)
const carsGroupsProcess         = computed(() => carsGroupsStore.carsGroupsProcess)
const carsGroupsMsg             = computed(() => carsGroupsStore.carsGroupsMsg)
const carsGroupsCreateMsg       = computed(() => carsGroupsStore.carsGroupsCreateMsg)
const carsGroupsDeleteProcess   = computed(() => carsGroupsStore.carsGroupsDeleteProcess)
const carsGroupsDeleteMsg       = computed(() => carsGroupsStore.carsGroupsDeleteMsg)

const viewMode              = ref(localStorage.getItem('UTA_GROUPS_VIEW') || 'lines') 
const pageNumber            = ref(1) 
const inputSearch           = ref('') 
const deleteGroupUUID       = ref(null) 
const showCreateCarsGroup  = ref(false) 
const showDeleteCarsGroup  = ref(false)

function changePage(cursor, dir) {

    const params = {
        'c.limit': 12, 
        'c.direction': dir, 
        'c.cursor': cursor
    }

    carsGroupsStore.readCarsGroups(params).then(() => {
        if(carsGroups.value.array) {
            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        }
    })
}

function setViewMode(mode) {

    viewMode.value = mode
    localStorage.setItem('UTA_GROUPS_VIEW', mode)
}

function submitDeleteCarsGroups() {

    carsGroupsStore.deleteCarsGroups(deleteGroupUUID.value)
}

carsGroupsStore.readCarsGroups({ 'c.limit': 12 })

watch(inputSearch, input => {

    input.length > 2 ? 
        carsGroupsStore.readCarsGroups({ 'c.limit': 12, 'c.q': inputSearch.value }) :
        carsGroupsStore.readCarsGroups({ 'c.limit': 12 })
})

watch(carsGroupsCreateMsg, msg => {

    if('success' in msg && msg.success) {

        carsGroupsStore.readCarsGroups({ 'c.limit': 12 })
        showCreateCarsGroup.value = false
    }
})

watch(carsGroupsDeleteMsg, msg => {

    if('success' in msg && msg.success) {

        carsGroupsStore.readCarsGroups({ 'c.limit': 12 })
        showDeleteCarsGroup.value = false
    }
})
</script>

<template>

    <div class="my-5 uta-cars-groups">

        <div class="container-fluid">

            <header class="page__header uta-header" role="heading" aria-level="1">
                <h2 class="uta-header__title">{{ getTranslate.CARS_GROUPS }}</h2>
                <div class="spinner-border uta-header__spinner" role="status" v-if="carsGroupsProcess && carsGroups.array?.length">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <div class="uta-header__actions">
                    <a 
                        href="#" 
                        class="uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon uta-header__action uta-header__action--create"
                        @click.prevent="showCreateCarsGroup = true"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons">add</span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_ADD }}</span>
                        </span>
                    </a>
                </div>
            </header>
        
            <div class="page__content">
                <div class="row mb-4 justify-content-between">
                    <div class="col-12 col-md-8 col-lg-10">
                        <div class="uta-input__wrapper uta-search">
                            <input 
                                type="text" 
                                :placeholder="getTranslate.SEARCH + '...'" 
                                v-model="inputSearch"
                                @keyup.escape="inputSearch = ''"
                                class="form-control uta-input__wrapper-input uta-search__input" 
                            >
                            <template v-if="inputSearch.length > 2">
                                <span class="material-icons uta-input__wrapper-icon" @click="inputSearch = ''">
                                    cancel
                                </span>
                            </template>
                            <template v-else>
                                <span class="material-icons uta-input__wrapper-icon">
                                    search
                                </span>
                            </template>
                        </div>
                    </div>
                    <div class="d-none d-md-flex view-selector">
                        <a href="#" class="view-selector__item view-selector__item--lines" @click.prevent="setViewMode('lines')"></a>
                        <a href="#" class="view-selector__item view-selector__item--grid" @click.prevent="setViewMode('grid')"></a>
                    </div>
                </div>
                <div class="loader loader--view-lg" v-if="carsGroupsProcess && !carsGroups.array?.length">
                    <div class="spinner-border text-secondary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div class="my-4 uta-alert uta-alert--success" v-if="'success' in carsGroupsCreateMsg && carsGroupsCreateMsg.success">
                    <span class="material-icons uta-alert__icon">check_circle_outline</span>
                    <div class="uta-alert__text">
                        {{ getTranslate[carsGroupsCreateMsg.text] || carsGroupsCreateMsg.text }}
                    </div>
                    <button type="button" class="uta-alert__close" @click="carsGroupsStore.setCarsGroupsCreateMsg({})">
                        <span class="material-icons">close</span>
                    </button>
                </div>
                <div class="my-4 uta-alert uta-alert--success" v-if="'success' in carsGroupsDeleteMsg && carsGroupsDeleteMsg.success">
                    <span class="material-icons uta-alert__icon">check_circle_outline</span>
                    <div class="uta-alert__text">
                        {{ getTranslate[carsGroupsDeleteMsg.text] || carsGroupsDeleteMsg.text }}
                    </div>
                    <button type="button" class="uta-alert__close" @click="carsGroupsStore.setCarsGroupsDeleteMsg({})">
                        <span class="material-icons">close</span>
                    </button>
                </div>
                <div 
                    class="my-4 uta-alert uta-alert--danger uta-group-users__message" 
                    v-if="'success' in carsGroupsMsg && !carsGroupsMsg.success"
                >
                    <span class="material-icons uta-alert__icon">error_outline</span>
                    <div class="uta-alert__text">
                        {{ getTranslate[carsGroupsMsg.text] || carsGroupsMsg.text }}
                    </div>
                </div>
                <template v-if="carsGroups.array?.length">
                    <template v-if="viewMode == 'lines'">
                        <div class="container-fluid">
                            <div class="uta-cars-groups__list uta-list">
                                <div class="uta-list__title d-none d-lg-block uta-cars-groups__title">
                                    <div class="row">
                                        <div class="col-10">
                                            <div class="row">
                                                <div class="col-6">{{ getTranslate.CARS_GROUPS_NAME }}</div>
                                                <div class="col-6">{{ getTranslate.CARS_GROUPS_DESCR }}</div>
                                            </div>
                                        </div>
                                        <div class="col-2"></div>
                                    </div>
                                </div>
                                <div class="pb-4 uta-list__content">
                                    <div 
                                        class="row align-items-center uta-list__item uta-cars-groups__group" 
                                        :class="{ 'uta-list__item--dark' : index % 2 }" 
                                        v-for="(group, index) in carsGroups.array" 
                                        :key="group.vin"
                                    >
                                        <div class="col-10">
                                            <div class="row">
                                                <div class="col-12 col-lg-6 uta-cars-groups__group-name">
                                                    <span class="d-inline d-lg-none uta-cars-groups__group-mobile-title">{{ getTranslate.CARS_GROUPS_NAME }}: </span>
                                                    <router-link :to="'/cars-groups/' + group.uuid" class="uta-list__item-link uta-cars-groups__link">{{ group.name }}</router-link>
                                                </div>
                                                <div class="col-12 col-lg-6 uta-cars-groups__group-descr">
                                                    <span class="d-inline d-lg-none uta-cars-groups__group-mobile-title">{{ getTranslate.CARS_GROUPS_DESCR }}: </span>
                                                    {{ group.description }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-2">
                                            <div class="text-end uta-cars-groups__group-delete">
                                                <span 
                                                    class="material-icons uta-list__item-icon uta-cars-groups__delete-icon"
                                                    @click="
                                                        showDeleteCarsGroup = true, 
                                                        deleteGroupUUID = group.uuid
                                                    "
                                                >
                                                    delete
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-if="viewMode == 'grid'">
                        <div class="row">
                            <div 
                                class="mb-4 col-24 col-sm-12 col-md-8 col-lg-6 col-xxl-4 uta-cars-groups__group grid" 
                                v-for="group in carsGroups.array" 
                                :key="group.uuid"
                            >
                                <div class="grid__item">
                                    <span 
                                        class="material-icons grid__item-icon" 
                                        @click="
                                            showDeleteCarsGroup = true, 
                                            deleteGroupUUID = group.uuid
                                        "
                                    >
                                        delete
                                    </span>
                                    <div class="grid__item-title">
                                        {{ group.name }}
                                    </div>
                                    <div class="grid__item-subtitle">
                                        {{ group.description || '-' }}
                                    </div>
                                    <router-link :to="'/cars-groups/' + group.uuid" class="grid__item-readmore">
                                        {{ getTranslate.BTN_READMORE }}
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </template>
                    <div class="row" v-if="carsGroups.c.isBefore || carsGroups.c.isAfter">
                        <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2">
                            <div class="uta-pagination">
                                <button 
                                    :disabled="carsGroupsProcess || !carsGroups.c.isBefore"
                                    class="uta-btn uta-btn--sm uta-pagination__prev" 
                                    @click.prevent="changePage(carsGroups.c.start, 'before')"
                                >
                                    <span class="material-icons">
                                        navigate_before
                                    </span>
                                </button>
                                <div class="uta-pagination__current">
                                    {{ pageNumber }}
                                </div>
                                <button 
                                    :disabled="carsGroupsProcess || !carsGroups.c.isAfter"
                                    class="uta-btn uta-btn--sm uta-pagination__next" 
                                    @click.prevent="changePage(carsGroups.c.stop, 'after')"
                                >
                                    <span class="material-icons">
                                        navigate_next
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
        <transition name="bounce">
            <Modal 
                v-if="showCreateCarsGroup"
                :header="getTranslate.CARS_GROUPS_CREATE"
                size="md"
                @closeModal="showCreateCarsGroup = false, carsGroupsStore.setCarsGroupsCreateMsg({})"
            >
                <template v-slot:body>
                    <UTACreateCarsGroup @closeModal="showCreateCarsGroup = false, carsGroupsStore.setCarsGroupsCreateMsg({})" />
                </template>
            </Modal>
        </transition>
        <transition name="bounce">
            <Modal 
                v-if="showDeleteCarsGroup"
                :header="getTranslate.CARS_GROUPS_DELETE"
                size="md"
                @closeModal="showDeleteCarsGroup = false"
            >
                <template v-slot:body>
                    <form action="" @submit.prevent="submitDeleteCarsGroups">
                        {{ getTranslate.CARS_GROUPS_DELETE_DESCR }}
                        <div class="mt-5 uta-btns">
                            <button type="button" class="uta-btn" @click="showDeleteCarsGroup = false">{{ getTranslate.BTN_CANCEL }}</button>
                            <button type="submit" class="uta-btn uta-btn--main" :disabled="carsGroupsDeleteProcess">{{ getTranslate.BTN_DELETE }}</button>
                        </div>
                    </form>
                </template>
            </Modal>
        </transition>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/cars-groups.scss';
</style>